import { useEffect, useRef, useState } from "react";
import Seo from "../../components/Seo";
import { routeMeta } from "../../seo/staticRoutes";
import ScrollToTop from "../../components/ScrollToTop";
import { verifyPasscode, isUnlocked, rememberUnlock, forgetUnlock } from "./passcode";
import "./trade-rates.css";

/**
 * Wholesale rate card — unlisted.
 *
 * Deliberately not linked from anywhere in the site: no nav entry, no footer
 * link, no sitemap entry, and robots.txt disallows the path. It is reachable
 * only by typing the URL, which is what was asked for — quiet, not secret.
 * Anyone who has the link can open it, so treat it as "not advertised" rather
 * than "protected".
 *
 * Rates below are the only source of truth on this page; nothing is derived or
 * inferred from the retail catalogue.
 */
const RATES = [
  {
    id: "zipper",
    name: "Zipper Pouch",
    rate: 35,
    unit: "pouch",
    note: "Resealable pouch format",
  },
  {
    id: "dhoop",
    name: "Dhoop Sticks & Cones",
    rate: 35,
    unit: "jar",
    note: "Jar format, sticks or cones",
  },
  {
    id: "agarbatti",
    name: "Agarbatti Jar",
    rate: 40,
    unit: "jar",
    note: "Jar format",
  },
  {
    id: "ganesh",
    name: "Ganesh Puja Kit",
    rate: 700,
    unit: "kit",
    note: "Complete kit, wholesale",
  },
];

const inr = (n) => "₹" + n.toLocaleString("en-IN");

/** Lock screen. See passcode.js for what this protects against, and what it doesn't. */
function PasscodeGate({ onUnlock }) {
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const [checking, setChecking] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = async (event) => {
    event.preventDefault();
    if (checking) return;

    setChecking(true);
    setError("");
    const ok = await verifyPasscode(value);
    setChecking(false);

    if (ok) {
      rememberUnlock();
      onUnlock();
    } else {
      setError("That passcode isn't right. Check for stray spaces or capitals.");
      setValue("");
      inputRef.current?.focus();
    }
  };

  return (
    <main className="tr-page tr-page--locked">
      <div className="tr-lock">
        <span className="tr-eyebrow">NT Sticks</span>
        <h1 className="tr-lock-title">Wholesale rate card</h1>
        <p className="tr-lock-copy">
          This page shows trade pricing and isn't part of the public shop. Enter the
          passcode to continue.
        </p>

        <form className="tr-lock-form" onSubmit={submit}>
          <label className="tr-lock-label" htmlFor="tr-passcode">Passcode</label>
          <input
            id="tr-passcode"
            ref={inputRef}
            type="password"
            className="tr-lock-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            autoComplete="current-password"
            aria-describedby={error ? "tr-lock-error" : undefined}
            aria-invalid={Boolean(error)}
          />
          <button type="submit" className="tr-lock-btn" disabled={checking || !value}>
            {checking ? "Checking…" : "Unlock"}
          </button>
        </form>

        {error && (
          <p className="tr-lock-error" id="tr-lock-error" role="alert">{error}</p>
        )}

        <p className="tr-lock-foot">
          Looking for our shop? <a href="/shop">Browse fragrances</a>
        </p>
      </div>
    </main>
  );
}

export default function TradeRates() {
  const [unlocked, setUnlocked] = useState(() => isUnlocked());
  const [qty, setQty] = useState({});

  const setQuantity = (id, value) => {
    const n = value === "" ? "" : Math.max(0, parseInt(value, 10) || 0);
    setQty((prev) => ({ ...prev, [id]: n }));
  };

  const lines = RATES.map((item) => {
    const count = Number(qty[item.id]) || 0;
    return { ...item, count, total: count * item.rate };
  });

  const grandTotal = lines.reduce((sum, l) => sum + l.total, 0);
  const totalUnits = lines.reduce((sum, l) => sum + l.count, 0);
  const hasQuote = totalUnits > 0;

  if (!unlocked) {
    return (
      <>
        <Seo {...routeMeta("/trade-rates")} />
        <PasscodeGate onUnlock={() => setUnlocked(true)} />
      </>
    );
  }

  return (
    <>
      <Seo {...routeMeta("/trade-rates")} />

      <main className="tr-page">
        <div className="tr-shell">

          <header className="tr-head">
            <span className="tr-eyebrow">Wholesale Rate Card</span>
            <h1 className="tr-title">Puja items, trade pricing</h1>
            <p className="tr-standfirst">
              Current wholesale rates for NT Sticks puja items, direct from our Indore factory.
              Enter quantities below to build a quote.
            </p>
            <p className="tr-unlisted">
              <span className="tr-dot" aria-hidden="true"></span>
              Unlisted page — not linked from the site and not indexed by search engines.
            </p>
          </header>

          <section className="tr-rates" aria-label="Wholesale rates">
            {RATES.map((item) => (
              <article className="tr-card" key={item.id}>
                <h2 className="tr-card-name">{item.name}</h2>
                <p className="tr-card-note">{item.note}</p>
                <p className="tr-card-rate">
                  <span className="tr-amount">{inr(item.rate)}</span>
                  <span className="tr-unit">per {item.unit}</span>
                </p>
              </article>
            ))}
          </section>

          <section className="tr-quote" aria-label="Build a quote">
            <div className="tr-quote-head">
              <h2>Build a quote</h2>
              <p>Quantities are not saved — this is a scratch pad for working out a total.</p>
            </div>

            <div className="tr-table-wrap">
              <table className="tr-table">
                <thead>
                  <tr>
                    <th scope="col">Item</th>
                    <th scope="col" className="tr-num">Rate</th>
                    <th scope="col" className="tr-num">Quantity</th>
                    <th scope="col" className="tr-num">Line total</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line) => (
                    <tr key={line.id}>
                      <th scope="row">
                        {line.name}
                        <span className="tr-row-unit">per {line.unit}</span>
                      </th>
                      <td className="tr-num">{inr(line.rate)}</td>
                      <td className="tr-num">
                        <input
                          type="number"
                          inputMode="numeric"
                          min="0"
                          step="1"
                          className="tr-qty"
                          value={qty[line.id] ?? ""}
                          placeholder="0"
                          onChange={(e) => setQuantity(line.id, e.target.value)}
                          aria-label={`Quantity of ${line.name}`}
                        />
                      </td>
                      <td className="tr-num tr-line-total">
                        {line.total ? inr(line.total) : <span className="tr-muted">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <th scope="row" colSpan="2">Total</th>
                    <td className="tr-num tr-muted">
                      {hasQuote ? `${totalUnits} units` : ""}
                    </td>
                    <td className="tr-num tr-grand">{inr(grandTotal)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {hasQuote && (
              <button
                type="button"
                className="tr-clear"
                onClick={() => setQty({})}
              >
                Clear quantities
              </button>
            )}
          </section>

          <footer className="tr-foot">
            <p>
              <strong>NT Sticks</strong> · Achhari Perfumery Works, Indore ·{" "}
              <a href="tel:+916264208349">+91 62642 08349</a>
            </p>
            <p className="tr-foot-note">
              Rates are ex-factory and exclude delivery. Confirm current pricing before
              committing to an order.
            </p>
            <button
              type="button"
              className="tr-lock-again"
              onClick={() => { forgetUnlock(); setUnlocked(false); }}
            >
              Lock this page
            </button>
          </footer>

        </div>
      </main>
      <ScrollToTop />
    </>
  );
}
